import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { writeFileXLSX, utils } from "xlsx";
import {
  Button,
  ContentBox,
  ContentPage,
  DrawerFilterPanel,
  DropdownAction,
  FullTable,
  InputTextarea,
  ModalDefault,
  ModalForm,
  NoResults,
  PageActions,
  Select,
  TableFilters,
  Text,
  Tooltip,
} from "@ficatranquilo/contabilidade.com-ui";
import useService from "../../../../hooks/useService";
import useListFilters from "../../../../hooks/useListFilters";
import ImportValuesModal from "./ImportValuesModal";
import { formatDate, maskDocument } from "../../../../helpers";
import { hasPermission } from "@helpers/permissions";
import { canOpenScreen } from "@helpers/companyScreens";
import { remunerationSummary } from "../../Common/ContractDetail";
import { DomainStatus, escapeHtml, formatCompetencia, usePageHeader } from "@components/ow";

// Página da API por requisição (o padrão do back)
const PAGE_SIZE = 50;

const STATUS_VALUE = { ACTIVE: "ativo", PAUSED: "pausado", TERMINATED: "encerrado" };

// Pílulas do TableFilters: uma por vez, "Todos" sem filtro
const STATUS_DATA = [
  { label: "Todos", value: "" },
  { label: "Ativos", value: "ACTIVE" },
  { label: "Pausados", value: "PAUSED" },
  { label: "Encerrados", value: "TERMINATED" },
];

const REMUNERATION_OPTIONS = [
  { value: "FIXED", label: "Valor fixo" },
  { value: "VARIABLE", label: "Valor lançado pela empresa" },
  { value: "HOURLY", label: "Por hora" },
  { value: "CLT", label: "CLT · marcação de ponto" },
];

const INITIAL_FILTERS = { status: STATUS_DATA[0] };

const BREADCRUMB = [{ page: "Contratos", route: "/c/contracts" }];

const ModalBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

export default function Contracts() {
  const navigate = useNavigate();
  const contractService = useService("contracts");
  const valueInputService = useService("contract-value-inputs");
  const projectService = useService("projects");
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: PAGE_SIZE });
  const [paginationData, setPaginationData] = useState({
    totalRegisters: 0,
    list: [],
    totalPages: 1,
  });
  const [loading, setLoading] = useState(true);
  const [selectedContract, setSelectedContract] = useState(null);
  const [terminationReason, setTerminationReason] = useState("");
  const [terminationError, setTerminationError] = useState("");
  const [loadingTerminate, setLoadingTerminate] = useState(false);
  const [showBulkValueInput, setShowBulkValueInput] = useState(false);
  const [pendingValueCount, setPendingValueCount] = useState(null);
  const [showExportModal, setShowExportModal] = useState(false);
  const [loadingExport, setLoadingExport] = useState(false);
  // Só a resposta da última requisição vale (buscas seguidas podem voltar fora de ordem)
  const lastRequest = useRef(0);
  const [projectOptions, setProjectOptions] = useState([]);
  // Total sem filtros: diferencia "nenhum contrato cadastrado" de "nada com estes filtros"
  const [hasAnyContract, setHasAnyContract] = useState(null);
  // "Ver contratos" só lê: encerrar, lançar e aprovar valores e criar seguem só ADM.
  // Com "Ver cronogramas", a lista de valores pendentes abre só para leitura.
  const auth = JSON.parse(localStorage.getItem("user_ow") || "{}");
  const isAdmin = auth?.role === "ADM";
  const canReadSchedules = hasPermission("payment_schedules:read");
  // Filtro de projeto só para quem abre a tela de projetos (o back responde a lista)
  const canListProjects = canOpenScreen("projects", auth);

  const {
    storeFilters,
    filters,
    filter,
    openFilter,
    setOpenFilter,
    handleSetFilters,
    handleConfirmFilter,
    handleClearFilters,
    hasFilter,
  } = useListFilters(INITIAL_FILTERS, setPagination);

  usePageHeader({ title: "Contratos", breadcrumb: BREADCRUMB });

  const statusValue = filters.status?.value || "";
  const searchTerm = (filters.search || "").trim();
  const filtersActive = !!statusValue || !!filters.remuneration || !!filters.project;

  useEffect(() => {
    fetchContracts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.pageIndex, pagination.pageSize, statusValue, filters.remuneration, filters.project, searchTerm]);

  useEffect(() => {
    if (isAdmin) fetchPendingCount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.pageIndex]);

  useEffect(() => {
    if (canListProjects) fetchProjects();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function fetchPendingCount() {
    try {
      const now = new Date();
      const result = await valueInputService.getPendingCount(now.getMonth() + 1, now.getFullYear());
      if (result && result.data && !result.error) {
        setPendingValueCount(result.data.count);
      }
    } catch (error) {
      // Silencioso - não bloqueia a página
    }
  }

  async function fetchProjects() {
    try {
      const result = await projectService.list("limit=9999&order=name:ASC");
      const list = result?.data?.list || [];
      setProjectOptions(list.map((p) => ({ value: p.id, label: p.name })));
    } catch (error) {
      // Sem a lista, o filtro de projeto só não aparece
    }
  }

  function buildQuery() {
    // Visão Empresa: sem scope=company o back devolve só os contratos de quem chama
    const params = [`scope=company`, `page=${pagination.pageIndex + 1}`, `limit=${pagination.pageSize}`];
    if (statusValue) params.push(`status=${statusValue}`);
    if (filters.remuneration) params.push(`remuneration_type=${filters.remuneration}`);
    if (filters.project) params.push(`project_id=${filters.project}`);
    if (searchTerm) params.push(`search=${encodeURIComponent(searchTerm)}`);
    return params.join("&");
  }

  async function fetchContracts() {
    const request = ++lastRequest.current;
    setLoading(true);
    try {
      const result = await contractService.list(buildQuery());
      if (request !== lastRequest.current) return;
      if (result && result.data) {
        setPaginationData(result.data);
        if (!filtersActive && !searchTerm) setHasAnyContract(result.data.totalRegisters > 0);
      } else {
        throw new Error("Erro ao carregar contratos");
      }
    } catch (error) {
      if (request !== lastRequest.current) return;
      console.error(error);
      const errorMessage = error?.response?.data?.msg || error?.message || "Erro ao carregar contratos";
      toast.error(errorMessage);
      setPaginationData({
        totalRegisters: 0,
        list: [],
        totalPages: 1,
      });
    }
    setLoading(false);
  }

  // Limpa busca, remuneração, projeto e também o status (o "Limpar filtros" do vazio)
  function clearAll() {
    handleClearFilters();
    storeFilters.setFilters({ status: STATUS_DATA[0] });
  }

  function handleTerminateClick(contract) {
    setSelectedContract(contract);
    setTerminationReason("");
    setTerminationError("");
  }

  function closeTerminateModal() {
    if (loadingTerminate) return;
    setSelectedContract(null);
    setTerminationError("");
  }

  async function handleTerminate() {
    if (!terminationReason.trim()) {
      setTerminationError("Informe o motivo do encerramento.");
      toast.error("Informe o motivo do encerramento");
      return;
    }

    setLoadingTerminate(true);
    try {
      const result = await contractService.terminateContract(selectedContract.id, terminationReason);

      if (result && !result.error) {
        toast.success("Contrato encerrado com sucesso!");
        setSelectedContract(null);
        setTerminationReason("");
        fetchContracts();
      } else {
        throw new Error(result?.msg || "Erro ao encerrar contrato");
      }
    } catch (error) {
      console.error(error);
      const errorMessage = error?.response?.data?.msg || error?.message || "Erro ao encerrar contrato";
      toast.error(errorMessage);
    }
    setLoadingTerminate(false);
  }

  async function handleExportExcel() {
    setLoadingExport(true);
    try {
      const result = await contractService.exportAll("scope=company");

      if (!result || result.error || !result.data) {
        throw new Error(result?.msg || "Erro ao buscar dados para exportação");
      }

      const contracts = Array.isArray(result.data) ? result.data : result.data.list || [];

      if (contracts.length === 0) {
        toast.info("Nenhum contrato encontrado para exportar");
        setLoadingExport(false);
        setShowExportModal(false);
        return;
      }

      const statusLabels = {
        ACTIVE: "Ativo",
        PAUSED: "Pausado",
        TERMINATED: "Encerrado",
      };

      const today = new Date();
      today.setHours(0, 0, 0, 0);

      function daysUntil(dateStr) {
        if (!dateStr) return "";
        const target = new Date(dateStr);
        if (isNaN(target.getTime())) return "";
        target.setHours(0, 0, 0, 0);
        return Math.ceil((target.getTime() - today.getTime()) / 86400000);
      }

      const sheetData = contracts.map((c) => ({
        "Prestador": c.contractor?.name || "-",
        "CPF/CNPJ": maskDocument(c.contractor?.cnpj || c.contractor?.document) || "-",
        "Razão Social": c.contractor?.company_name || "-",
        "Status": statusLabels[c.status] || c.status || "-",
        "Data Inicial": c.start_date ? formatDate(c.start_date) : "-",
        "Data Final": c.renewal_date ? formatDate(c.renewal_date) : "",
        "Dias até o Vencimento": daysUntil(c.renewal_date),
      }));

      const ws = utils.json_to_sheet(sheetData);

      ws["!cols"] = [
        { wch: 30 },
        { wch: 20 },
        { wch: 30 },
        { wch: 12 },
        { wch: 14 },
        { wch: 14 },
        { wch: 20 },
      ];

      const wb = utils.book_new();
      utils.book_append_sheet(wb, ws, "Contratos");

      const now = new Date();
      const dateStr = `${String(now.getDate()).padStart(2, "0")}_${String(now.getMonth() + 1).padStart(2, "0")}_${now.getFullYear()}`;
      writeFileXLSX(wb, `contratos_${dateStr}.xlsx`);

      toast.success("Exportação realizada com sucesso!");
      setShowExportModal(false);
    } catch (error) {
      console.error(error);
      const errorMessage =
        error?.response?.data?.msg ||
        error?.message ||
        "Erro ao exportar contratos";
      toast.error(errorMessage);
    }
    setLoadingExport(false);
  }

  const detailsRoute = (contract) => `/c/contracts/${contract.id}`;

  const columns = [
    {
      id: "contractor",
      header: "Prestador",
      size: 280,
      maxSize: 360,
      cell: ({ row }) => (
        <div>
          <Text $text={escapeHtml(row.original.contractor?.name || "-")} $variant="paragraph_small_bold" $color="N900" />
          <Text
            $text={escapeHtml(row.original.contractor?.company_name || row.original.contractor?.email || "")}
            $variant="caption_medium"
            $color="N400"
          />
        </div>
      ),
    },
    { id: "remuneration", header: "Remuneração", size: 220, maxSize: 260, cell: ({ row }) => remunerationSummary(row.original) },
    { id: "project", header: "Projeto", size: 160, cell: ({ row }) => row.original.project?.name || "-" },
    { id: "start", header: "Início", size: 110, cell: ({ row }) => formatDate(row.original.start_date) || "-" },
    {
      id: "status",
      header: "Status",
      size: 120,
      cell: ({ row }) => <DomainStatus domain="contrato" value={STATUS_VALUE[row.original.status] || row.original.status} />,
    },
    {
      id: "actions",
      // O "Ações" da lib tem 70px; abaixo disso a seta sai da célula
      size: 80,
      minSize: 80,
      maxSize: 80,
      align: "right",
      cell: ({ row }) => (
        <DropdownAction
          options={[
            { label: "Ver detalhes", action: () => navigate(detailsRoute(row.original)) },
            {
              label: "Encerrar contrato",
              action: () => handleTerminateClick(row.original),
              isImportant: true,
              show: isAdmin && row.original.status === "ACTIVE",
            },
          ]}
        />
      ),
    },
  ];

  const searching = !!searchTerm;
  const noContractsAtAll = !loading && !filtersActive && !searching && paginationData.totalRegisters === 0;

  const noResults = noContractsAtAll ? (
    <NoResults
      notResultText="Nenhum contrato cadastrado"
      includeText={isAdmin ? "Novo contrato" : undefined}
      includeAction={isAdmin ? () => navigate("/c/contracts/new") : undefined}
    />
  ) : (
    <NoResults notResultText="Nenhum contrato encontrado" includeText="Limpar filtros" includeAction={clearAll} />
  );

  const importLabel = pendingValueCount > 0 ? `Importar valores (${pendingValueCount})` : "Importar valores";
  // O contador é sempre do mês atual (fetchPendingCount); o texto é o do aviso do modal
  const today = new Date();
  const importTitle =
    pendingValueCount > 0
      ? `${pendingValueCount} ${pendingValueCount === 1 ? "contrato variável" : "contratos variáveis"} sem valor em ${formatCompetencia(
          { month: today.getMonth() + 1, year: today.getFullYear() },
          true
        )}`
      : undefined;
  // O `Button` da lib fixa o `title` no próprio texto: a competência vai no `Tooltip`
  const importButton = <Button $text={importLabel} $variant="outline" $action={() => setShowBulkValueInput(true)} />;

  return (
    <ContentPage>
      {canReadSchedules || isAdmin ? (
        <PageActions>
          {canReadSchedules && (
            <Button
              $text={isAdmin ? "Aprovar valores" : "Valores pendentes"}
              $variant="outline"
              $action={() => navigate("/c/contracts/value-approvals")}
            />
          )}
          {isAdmin && (
            <>
              {importTitle ? (
                <Tooltip content={importTitle} $position="bottom" $size="medium" $color="dark">
                  {importButton}
                </Tooltip>
              ) : (
                importButton
              )}
              <Button $text="Novo contrato" $variant="primary" $action={() => navigate("/c/contracts/new")} />
            </>
          )}
        </PageActions>
      ) : null}

      <ContentBox>
        {/* Sem nenhum contrato, não há o que filtrar */}
        {hasAnyContract !== false || filtersActive || searching ? (
          <TableFilters
            storeFilters={storeFilters}
            searchTerm="search"
            searchPlaceholder="Buscar por prestador, razão social, e-mail ou CPF/CNPJ"
            statusData={STATUS_DATA}
            filter={filter}
            handleSetFilters={handleSetFilters}
            handleClearFilters={handleClearFilters}
            setStatePagination={setPagination}
            openFilter={() => setOpenFilter((prev) => !prev)}
            exportData={() => setShowExportModal(true)}
            hasFilter={hasFilter}
          />
        ) : null}

        <FullTable
          data={paginationData.list}
          columns={columns}
          rowCount={paginationData.totalRegisters}
          pagination={pagination}
          setPagination={setPagination}
          getRowId={(row) => String(row.id)}
          isLoading={loading}
          onRowClick={(contract) => navigate(detailsRoute(contract))}
          noResults={noResults}
        />
      </ContentBox>

      <DrawerFilterPanel
        $showDrawer={openFilter}
        $setShowDrawer={() => setOpenFilter((prev) => !prev)}
        $confirmFilter={handleConfirmFilter}
        $clearFilters={handleClearFilters}
        $hasFilter={hasFilter}
      >
        <Select
          $idForm="remuneration"
          $label="Remuneração"
          $options={REMUNERATION_OPTIONS}
          $isClearable
          value={REMUNERATION_OPTIONS.find((o) => o.value === filter.remuneration) || null}
          onChange={(option) => handleSetFilters(null, "remuneration", option ? option.value : "")}
        />
        {canListProjects && projectOptions.length > 0 ? (
          <Select
            $idForm="project"
            $label="Projeto"
            $options={projectOptions}
            $isClearable
            value={projectOptions.find((o) => o.value === filter.project) || null}
            onChange={(option) => handleSetFilters(null, "project", option ? option.value : "")}
          />
        ) : null}
      </DrawerFilterPanel>

      {showExportModal && (
        <ModalDefault
          $showModal={showExportModal}
          $setShowModal={() => !loadingExport && setShowExportModal(false)}
          $noClose={loadingExport}
          $size="medium"
          $title="Exportar contratos"
          $contentBackground="N0"
          $footer={
            <>
              <Button
                $text="Cancelar"
                $variant="outline"
                $action={() => setShowExportModal(false)}
                $isDisabled={loadingExport}
                $noWidth
              />
              <Button $text="Exportar" $variant="primary" $action={handleExportExcel} $isLoading={loadingExport} $noWidth />
            </>
          }
        >
          <Text
            $text="Deseja exportar os contratos da empresa para uma planilha Excel? Todos os contratos serão exportados, ordenados pelo vencimento mais próximo."
            $variant="paragraph_small_medium"
            $color="N700"
          />
        </ModalDefault>
      )}

      {showBulkValueInput && (
        <ImportValuesModal
          onClose={(imported) => {
            setShowBulkValueInput(false);
            if (imported) {
              fetchContracts();
              fetchPendingCount();
            }
          }}
        />
      )}

      {selectedContract && (
        <ModalForm
          $showModal={!!selectedContract}
          $setShowModal={closeTerminateModal}
          $noClose={loadingTerminate}
          $isLoading={loadingTerminate}
          $size="medium"
          $title="Encerrar contrato"
          $contentBackground="N0"
          $actions={[
            { text: "Cancelar", action: closeTerminateModal, variant: "outline", isDisabled: loadingTerminate },
            { text: "Encerrar contrato", action: handleTerminate, variant: "danger", isDisabled: loadingTerminate },
          ]}
        >
          <ModalBody>
            <Text
              $text="Você está prestes a encerrar o contrato de <strong>%s</strong>."
              $replaces={[escapeHtml(selectedContract.contractor?.name || "prestador")]}
              $variant="paragraph_small_medium"
              $color="N700"
              $colorEmphasis="N900"
            />
            <InputTextarea
              $idForm="termination-reason"
              $label="Motivo do encerramento"
              $variant="default"
              $isRequired
              $errors={terminationError}
              rows={3}
              value={terminationReason}
              onChange={(e) => {
                setTerminationReason(e.target.value);
                if (terminationError) setTerminationError("");
              }}
              placeholder="Ex.: fim do projeto, rescisão por acordo das partes"
            />
          </ModalBody>
        </ModalForm>
      )}
    </ContentPage>
  );
}
